import { cn } from '@optools/components/ui/utils';
import { IThumbProps } from 'react-range/lib/types';

/**
 * Props for the MilestoneSliderThumb component.
 */
export interface MilestoneSliderThumbProps {
  /**
   * Determines if the tooltip should always be shown.
   * @default false
   */
  alwaysShowTooltip?: boolean;

  /**
   * The index of the thumb.
   */
  index: number;

  /**
   * Indicates if the thumb is currently being dragged.
   */
  isDragged: boolean;

  /**
   * Callback function for when the thumb is clicked to start dragging.
   * @param value - The current value of the thumb.
   * @param index - The index of the thumb.
   */
  onDragThumb?: (value: number, index: number) => void;

  /**
   * Callback function for when the thumb is hovered.
   * @param value - The current value of the thumb.
   * @param index - The index of the thumb.
   */
  onHoverThumb?: (value: number, index: number) => void;

  /**
   * Additional properties for the thumb.
   */
  props: IThumbProps;

  /**
   * Function to render the tooltip label.
   * @param value - The current value of the thumb.
   * @param index - The index of the thumb.
   * @param isDragged - Indicates if the thumb is currently being dragged.
   * @returns A React node to be displayed as the tooltip label.
   */
  renderTooltipLabel: (value: number, index: number, isDragged: boolean) => React.ReactNode;

  /**
   * The current value of the thumb.
   */
  value: number;
}

export function MilestoneSliderThumb({
  alwaysShowTooltip = false,
  index,
  isDragged,
  onDragThumb,
  onHoverThumb,
  props,
  renderTooltipLabel,
  value,
}: MilestoneSliderThumbProps) {
  return (
    <button
      {...props}
      key={props.key}
      className={cn(
        'group relative block size-3 shrink-0 rounded-full border border-ring bg-white ring-ring/50 transition-[color,box-shadow] select-none after:absolute after:-inset-2 hover:ring-3 focus-visible:ring-3 focus-visible:outline-hidden active:ring-3 disabled:pointer-events-none disabled:opacity-50',
        isDragged ? 'z-999!' : 'hover:z-999!',
      )}
      style={{ ...props.style, zIndex: index }}
      onMouseDown={() => onDragThumb?.(value, index)}
      onMouseEnter={() => (onHoverThumb ? onHoverThumb(value, index) : undefined)}>
      <div
        className={cn(
          'absolute bottom-6 left-1/2 z-50 -translate-x-1/2 transform overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md',
          {
            'invisible group-hover:visible': !alwaysShowTooltip,
            visible: isDragged,
          },
        )}>
        {renderTooltipLabel(value, index, isDragged)}
      </div>
    </button>
  );
}
